/**
 * Fluxible actions for use in the delete flow.
 */
import _ from 'lodash';
import type Q from 'q';

import type { ActionContext } from 'js/lib/ActionContext';

import _t from 'i18n!nls/staff-management';

import type { NotificationParam } from '../../common/notifications/Notifications';
import { EVENT_NAMES } from '../constants';
import * as staffMembershipsSelectors from '../selectors/staffMemberships';
import type { StaffManagementState } from '../store';
import type { IdentifiableInterface, ObjectId, StaffMembershipInterface, StaffMembershipType } from '../types';
import { del } from './core';

/**
 * Set the list of people who are candidates for having their memberships deleted.
 *
 * Calling with no args acts as an unset.
 *
 * This will trigger a modal in the flow, forcing user to confirm or abort the delete.
 */
export const prompt = (
  /** Provided by fluxible. */
  actionContext: ActionContext,
  /** Users opted in for deletion, particular entity id to unlink from, and type of entity. */
  {
    fromId,
    memberships = [],
    type,
    users = [],
  }: {
    fromId?: string;
    memberships?: Array<ObjectId>;
    type?: keyof typeof StaffMembershipType;
    users?: Array<IdentifiableInterface>;
  } = {}
): void => {
  actionContext.dispatch(EVENT_NAMES.UI, { delFlow: { fromId, memberships, type, users } });
};

/**
 * Mark specific users for having their type specific memberships removed, e.g. only remove
 * institution roles for the `partner` type.
 *
 * Delegates to prompt.
 */
export const promptTypeRolesOnly = (
  /** Provided by fluxible. */
  actionContext: ActionContext,
  /** Users opted in for deletion, particular entity id to unlink from, and type of entity. */
  {
    fromId,
    type,
    users = [],
  }: {
    fromId?: string;
    type?: keyof typeof StaffMembershipType;
    users?: Array<IdentifiableInterface>;
  } = {}
): void => {
  const state: StaffManagementState = actionContext.getStore('StaffManagementStoreV2').get();
  const membershipsByUserIdLimitedToType = staffMembershipsSelectors.getMembershipsByUserIdLimitedByType(state);

  let memberships = _.filter(membershipsByUserIdLimitedToType, (userMemberships, userId) => {
    return _.find(users, (user) => _.toString(user.id) === userId);
  });
  memberships = _.flatten(memberships).map((membership) => membership.id);
  actionContext.executeAction(prompt, { fromId, memberships, type, users });
};

/**
 * Delete memberships from a set of users.
 *
 * If `memberships` are passed, only those specific memberships will be deleted. If
 * `memberships` are not passed, all memberships for the users within the management
 * scope of the component will be deleted.
 */
export const confirm = (
  /** Provided by fluxible. */
  actionContext: ActionContext,
  /** List of users from which to delete memberships from, and the type of memberships. */
  {
    memberships,
    users,
    // API DI.
    _del = del,
    notify,
  }: {
    memberships?: Array<ObjectId>;
    users?: Array<IdentifiableInterface>;
    _del?(actionContext: ActionContext, { ids }: { ids?: ObjectId | Array<ObjectId> }): Q.Promise<void>;
    notify: (notification: NotificationParam) => void;
  }
) => {
  const state: StaffManagementState = actionContext.getStore('StaffManagementStoreV2').get();
  const resolvedUsers = users || state.ui?.delFlow?.users;
  const resolvedMemberships = memberships || state.ui?.delFlow?.memberships;

  let ids: Array<ObjectId> = _.clone(resolvedMemberships) || [];
  // Find all memberships related to the batch of users and delete them.
  if (_.isEmpty(ids)) {
    const userIds = _.keyBy(resolvedUsers, 'id');
    const allMemberships = state.data?.memberships;
    ids = _.reduce(
      allMemberships,
      (toBeDeleted, membership: StaffMembershipInterface) => {
        if (membership.userId in userIds) {
          toBeDeleted.push(membership.id);
        }
        return toBeDeleted;
      },
      ids
    );
  }

  actionContext.executeAction(prompt);
  return actionContext
    .executeAction(_del, { ids })
    .then(() => {
      notify({ message: _t('Removal successful.'), type: 'success' });
    })
    .catch(() => {
      notify({
        message: _t('Error during removal. Recommend refreshing your browser and trying again.'),
        type: 'error',
      });
    });
};
